import React, { useMemo, useRef, useState } from "react";
import { Pressable, StyleSheet, View } from "react-native";
import { LocalizedText as Text, useExhibitLanguage } from "../i18n/ExhibitLocaleContext";
import { tx } from "../i18n/exhibitLocale";
import {
  TRAIN_LAB_TESTS,
  buildTrainLabVariants,
  classifyTrainLab,
} from "../models/trainLabModel";

function t(value, tier) {
  if (typeof value === "string") return value;
  if (tier === "el") return value.el ?? value.ms ?? value.hs;
  if (tier === "ms") return value.ms ?? value.hs ?? value.el;
  return value.hs ?? value.ms ?? value.el;
}

const FRIENDLY_VARIANT_COPY = {
  "narrow-brief": {
    group: "PETS ONLY",
    practice: "Brief practice",
    detail: "8 examples · cats and dogs only",
  },
  "narrow-long": {
    group: "PETS ONLY",
    practice: "More practice",
    detail: "Same 8 examples · cats and dogs only",
  },
  "wide-brief": {
    group: "MORE ANIMAL TYPES",
    practice: "Brief practice",
    detail: "8 examples · 8 animal types",
  },
  "wide-long": {
    group: "MORE ANIMAL TYPES",
    practice: "More practice",
    detail: "Same 8 examples · 8 animal types",
  },
};

export default function TrainLabScreen({ tier = "hs", onBack = () => {}, onAttempt = async () => null, onNarrationMoment = () => {} }) {
  const language = useExhibitLanguage();
  const variants = useMemo(() => buildTrainLabVariants(), []);
  // Fox is the clearest default for the visitor story:
  // narrow data misses it even with more practice; wider data needs enough practice.
  const [picked, setPicked] = useState(4);
  const [countedTests, setCountedTests] = useState({});
  const [showHow, setShowHow] = useState(false);
  const takeawayNarratedRef = useRef(false);
  const test = TRAIN_LAB_TESTS[picked];
  const testName = tx(language, test.name);

  const chooseTest = (index) => {
    const item = TRAIN_LAB_TESTS[index];
    if (item && item.label !== "cat" && item.label !== "dog" && !countedTests[index]) {
      setCountedTests((old) => ({ ...old, [index]: true }));
    }
    if (item) {
      onAttempt({
        stationId: "train-lab",
        itemId: "test_animal",
        selectedAnswer: item.label,
        correctAnswer: null,
        isCorrect: null,
        attemptType: "model_test_selection",
        metadata: { animal: item.name, experimentVersion: "controlled_2x2_v1" },
      });
    }
    setPicked(index);
    if (!takeawayNarratedRef.current) {
      takeawayNarratedRef.current = true;
      onNarrationMoment("v2_train_lab_takeaway");
    }
  };

  const results = variants.map((variant) => ({
    variant,
    result: classifyTrainLab(variant, test),
  }));

  const narrowBrief = results[0].result;
  const narrowLong = results[1].result;
  const wideBrief = results[2].result;
  const wideLong = results[3].result;

  let observation;
  if (test.label === "cat" || test.label === "dog") {
    observation = t({
      el: "Cats and dogs were included in both sets of examples, so this test does not show the difference very clearly. Try the fox, wolf, or tiger.",
      hs: "Cats and dogs appear in both training sets, so this case does not reveal much about variety. Try the fox, wolf, or tiger instead.",
    }, tier);
  } else if (!narrowBrief.correct && !narrowLong.correct && !wideBrief.correct && wideLong.correct) {
    observation = language === "es"
      ? (tier === "el"
        ? `Para este ${testName.toLowerCase()}, practicar más tiempo por sí solo no fue suficiente. El modelo necesitó ejemplos de más tipos de animales — y suficiente práctica con ellos — antes de acertar.`
        : `Para este ${testName.toLowerCase()}, practicar más con ejemplos solo de mascotas no fue suficiente. El modelo necesitó ejemplos más variados y suficiente práctica con ellos antes de acertar.`)
      : t({
          el: `For this ${test.name.toLowerCase()}, more practice alone was not enough. The model needed examples from more kinds of animals — and enough practice with those examples — before it got the answer right.`,
          hs: `For this ${test.name.toLowerCase()}, more practice with pets-only examples was not enough. The model needed broader examples and enough practice with them before it got the answer right.`,
        }, tier);
  } else if (!narrowBrief.correct && !narrowLong.correct && wideBrief.correct && wideLong.correct) {
    observation = language === "es"
      ? (tier === "el"
        ? `Los modelos entrenados solo con mascotas fallan con este ${testName.toLowerCase()}, pero los modelos que aprendieron de más tipos de animales aciertan incluso con práctica breve.`
        : `Los modelos entrenados solo con mascotas fallan con este ${testName.toLowerCase()}, mientras que los ejemplos más variados bastan para que ambos modelos con datos amplios lo clasifiquen correctamente.`)
      : t({
          el: `The pets-only models miss this ${test.name.toLowerCase()}, but the models that learned from more kinds of animals get it right — even with brief practice.`,
          hs: `The pets-only models miss this ${test.name.toLowerCase()}, while the broader examples are enough for both wider-data models to classify it correctly.`,
        }, tier);
  } else if (wideLong.correct && !narrowLong.correct) {
    observation = language === "es"
      ? (tier === "el"
        ? `Tener más tipos de ejemplos marcó la mayor diferencia para este ${testName.toLowerCase()}. Practicar más no podía enseñar al modelo de mascotas un tipo de animal que nunca había visto.`
        : `Los ejemplos más variados marcan la diferencia clave para este ${testName.toLowerCase()}. Más práctica no puede reemplazar tipos de animales que faltan en los ejemplos.`)
      : t({
          el: `More kinds of examples made the biggest difference for this ${test.name.toLowerCase()}. Extra practice could not teach the pets-only model an animal type it never learned from.`,
          hs: `Broader examples make the key difference for this ${test.name.toLowerCase()}. More practice cannot replace animal types that are missing from the examples.`,
        }, tier);
  } else {
    observation = t({
      el: "This small model does not behave perfectly on every animal. That is part of the lesson: more practice and more variety can help, but neither guarantees every answer.",
      hs: "This small model does not improve perfectly on every test. More practice and broader examples can help, but neither guarantees every prediction.",
    }, tier);
  }

  return (
    <View style={s.container}>
      <Top onBack={onBack} />
      <View style={s.columns}>
        <View style={s.left}>
          <Text style={s.title}>{t({ el: "What helps AI learn?", hs: "What helps AI learn?" }, tier)}</Text>
          <Text style={s.subtitle}>{t({
            el: "Same challenge. Four tiny AI models. We change two things: how long they practice and how many kinds of animals they learn from.",
            hs: "Same challenge. Four tiny AI models. We change two things: how long they practice and how many kinds of animals they learn from.",
          }, tier)}</Text>

          <Text style={s.pickPrompt}>Pick an animal to test all four models:</Text>
          <View style={s.testRow}>
            {TRAIN_LAB_TESTS.map((item, index) => (
              <Pressable key={item.name} onPress={() => chooseTest(index)} style={[s.testCard, picked === index && s.testPicked]}>
                <Text style={s.testIcon}>{item.icon}</Text>
                <Text style={s.testName}>{item.name}</Text>
              </Pressable>
            ))}
          </View>

          <Text style={s.comparePrompt}>COMPARE THE FOUR AI GUESSES · THESE BOXES ARE RESULTS, NOT BUTTONS</Text><View style={s.grid}>
            {results.map(({ variant, result }) => {
              const copy = FRIENDLY_VARIANT_COPY[variant.key];
              return (
                <View key={variant.key} style={[s.card, result.correct ? s.goodBorder : s.warnBorder]}>
                  <Text style={s.cardKicker}>{copy.group}</Text>
                  <Text style={s.cardTitle}>{copy.practice}</Text>
                  <Text style={s.examples}>{copy.detail}</Text>
                  <Text style={s.guessLabel}>MODEL GUESSES</Text>
                  <Text style={s.answer}>{result.name}</Text>
                  <Text style={[s.conf, result.correct ? s.good : s.warn]}>{result.correct ? "✓ right" : "✕ not this time"}</Text>
                </View>
              );
            })}
          </View>
        </View>

        <View style={s.right}>
          <Text style={s.eyebrow}>WHAT HAPPENED?</Text>
          <Text style={s.lesson}>{observation}</Text>

          <View style={s.takeaway}>
            <Text style={s.takeHead}>THE TAKEAWAY</Text>
            <Text style={s.takeText}>{t({
              el: "Practicing longer can help. Seeing a wider variety of examples can help too. But practicing longer cannot make up for kinds of examples the AI never learned from.",
              hs: "More practice can help a model learn from the examples it has. Broader examples can help it handle more kinds of cases. More practice cannot replace important examples that are missing.",
            }, tier)}</Text>
          </View>


          <Pressable onPress={() => setShowHow((value) => !value)} style={s.howButton}>
            <Text style={s.howButtonText}>{showHow ? "Hide how it works" : "How does this work?"}</Text>
          </Pressable>

          {showHow ? (
            <View style={s.howBox}>
              <Text style={s.howText}>This simplified model really runs in the browser. Each animal is represented by six traits: size, ear shape, snout length, mane, stripes, and spots. The emoji is simply the picture shown to you; the model learns from those traits.</Text>
            </View>
          ) : null}

          <Pressable onPress={onBack} style={s.button}><Text style={s.buttonText}>Back to the menu →</Text></Pressable>
        </View>
      </View>
    </View>
  );
}

function Top({ onBack }) {
  return <View style={s.top}><Pressable onPress={onBack}><Text style={s.topText}>← Menu</Text></Pressable><Text style={s.topText}>What Helps AI Learn?</Text></View>;
}

const s = StyleSheet.create({
  container:{width:1920,height:1080,backgroundColor:"#070D17"},top:{height:72,paddingHorizontal:48,flexDirection:"row",gap:26,alignItems:"center"},topText:{color:"#8FA5BC",fontSize:19,fontWeight:"800"},columns:{flexDirection:"row",gap:44,paddingHorizontal:90},left:{width:1160},right:{flex:1,paddingTop:40},title:{marginTop:12,color:"#F4F8FC",fontSize:52,fontWeight:"900"},subtitle:{marginTop:8,color:"#9FB2C6",fontSize:21,lineHeight:30,maxWidth:1080},pickPrompt:{marginTop:18,color:"#C9D6E4",fontSize:18,fontWeight:"800"},testRow:{marginTop:10,flexDirection:"row",gap:12},testCard:{width:170,height:104,borderRadius:16,borderWidth:1,borderColor:"#1B2A44",backgroundColor:"#0D1626",alignItems:"center",justifyContent:"center"},testPicked:{borderWidth:2,borderColor:"#4FD1FF",backgroundColor:"#0E2237"},testIcon:{fontSize:38},testName:{marginTop:3,color:"#F4F8FC",fontSize:16,fontWeight:"800"},comparePrompt:{marginTop:18,color:"#8FA5BC",fontSize:14,fontWeight:"900",letterSpacing:1.2},grid:{marginTop:10,flexDirection:"row",flexWrap:"wrap",gap:14},card:{width:555,minHeight:225,borderRadius:20,borderWidth:1,backgroundColor:"#0D1626",padding:22},goodBorder:{borderColor:"#244C43"},warnBorder:{borderColor:"#604335"},cardKicker:{color:"#8FA5BC",fontSize:14,fontWeight:"900",letterSpacing:1.4},cardTitle:{marginTop:5,color:"#F4F8FC",fontSize:27,fontWeight:"900"},examples:{marginTop:5,color:"#7F93A8",fontSize:16},guessLabel:{marginTop:20,color:"#5F7690",fontSize:13,fontWeight:"900",letterSpacing:1.2},answer:{marginTop:3,color:"#4FD1FF",fontSize:38,fontWeight:"900"},conf:{marginTop:4,fontSize:18,fontWeight:"800"},good:{color:"#5ADC9B"},warn:{color:"#FBBF24"},eyebrow:{color:"#4FD1FF",fontSize:16,fontWeight:"900",letterSpacing:1.8},lesson:{marginTop:12,color:"#C9D6E4",fontSize:23,lineHeight:35},takeaway:{marginTop:24,borderRadius:18,borderWidth:1,borderColor:"#1B2A44",backgroundColor:"#0D1626",padding:22},takeHead:{color:"#FBBF24",fontSize:16,fontWeight:"900",letterSpacing:1.5},takeText:{marginTop:10,color:"#D8E2EC",fontSize:21,lineHeight:32},honesty:{marginTop:20,color:"#8FA5BC",fontSize:16,lineHeight:24},howButton:{marginTop:14,alignSelf:"flex-start",borderBottomWidth:1,borderBottomColor:"#4FD1FF",paddingBottom:2},howButtonText:{color:"#4FD1FF",fontSize:16,fontWeight:"800"},howBox:{marginTop:10,borderRadius:14,borderWidth:1,borderColor:"#1B2A44",backgroundColor:"#091321",padding:15},howText:{color:"#9FB2C6",fontSize:15,lineHeight:22},button:{marginTop:20,alignSelf:"flex-start",backgroundColor:"#0E3A50",borderWidth:1,borderColor:"#4FD1FF",borderRadius:14,paddingHorizontal:26,paddingVertical:15},buttonText:{color:"#F4F8FC",fontSize:19,fontWeight:"900"}
});
